import "./BugReportModal.scss";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import FlowTextInput from "../../FlowTextInput/FlowTextInput";
import { submitBugReport } from "../../../api/bugReports/bugReports";
import useAuthStore from "../../../store/authStore";

function BugReportModal({ onClose }) {
  const { t } = useTranslation();
  const [reportType, setReportType] = useState("bug"); // 'bug' or 'feature'
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const titleInputRef = useRef(null);
  const user = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);

  // Auto-focus title input when modal opens
  useEffect(() => {
    const timer = setTimeout(() => {
      titleInputRef.current?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !description.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await submitBugReport({
        type: reportType,
        title: title.trim(),
        description: description.trim(),
        userEmail: user?.email || "",
        userName:
          `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
          user?.email ||
          "",
        token,
      });

      // Clear form and close modal on success
      setTitle("");
      setDescription("");
      onClose();
    } catch (error) {
      console.error("Failed to submit bug report:", error);
      alert(
        reportType === "bug"
          ? t("bugReport.bugFailed")
          : t("bugReport.featureFailed")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    setTitle("");
    setDescription("");
    onClose();
  };

  return (
    <div className="bug-report-modal-content">
      <div className="title">
        <span>{t("bugReport.title")}</span>
        <button className="close-button" onClick={onClose}>
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      <div className="main-wrapper">
        <div className="content">
          <div className="type-selector">
            <button
              className={`type-button ${reportType === "bug" ? "active" : ""}`}
              onClick={() => setReportType("bug")}
            >
              <span className="material-symbols-outlined">bug_report</span>
              {t("bugReport.bugReport")}
            </button>
            <button
              className={`type-button ${reportType === "feature" ? "active" : ""}`}
              onClick={() => setReportType("feature")}
            >
              <span className="material-symbols-outlined">lightbulb</span>
              {t("bugReport.featureRequest")}
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="title">{t("common.title")}</label>
              <FlowTextInput
                ref={titleInputRef}
                placeholder={
                  reportType === "bug"
                    ? t("bugReport.bugTitlePlaceholder")
                    : t("bugReport.featureTitlePlaceholder")
                }
                value={title}
                onChange={setTitle}
                expandable={false}
                icon=""
                className="bug-report-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">{t("common.description")}</label>
              <textarea
                id="description"
                className="form-textarea"
                placeholder={
                  reportType === "bug"
                    ? t("bugReport.bugDescriptionPlaceholder")
                    : t("bugReport.featureDescriptionPlaceholder")
                }
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={8}
                required
              />
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="action-button clear"
                onClick={handleClear}
                disabled={isSubmitting}
              >
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                className="action-button submit"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? t("bugReport.submitting")
                  : reportType === "bug"
                    ? t("bugReport.submitBug")
                    : t("bugReport.submitFeature")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default BugReportModal;
